import React from 'react';
import { Box, Divider } from '@mui/material';
import type { Header } from '@tanstack/react-table';
import type { ColumnMetadata } from '../../../types';
import type { FilterOperators, FilterMenuAnchor } from '../types';
import {
  formatDateTimeForInput,
  toBackendDateString,
  toBackendDateTimeString,
} from '../../../utils';
import { filterCellBoxSx, filterDividerSx } from './filterLayoutStyles';
import { OperatorIconButton } from './OperatorIconButton';
import {
  CustomDatePicker,
  DATE_PICKER_MODE,
} from '../../../../../components/common/CustomDatePicker';

type DateFilterInputProps<T> = {
  header: Header<T, unknown>;
  columnMetadata?: ColumnMetadata;
  filterValue: string;
  filterOperators: FilterOperators;
  setFilterMenuAnchor: React.Dispatch<React.SetStateAction<FilterMenuAnchor>>;
};

const resolveDateFilterMode = (metadata?: ColumnMetadata): 'date' | 'datetime' => {
  const type = metadata?.type || metadata?.dataType;
  return type === 'date' ? DATE_PICKER_MODE.DATE : DATE_PICKER_MODE.DATETIME;
};

const DateFilterInputComponent = <T,>({
  header,
  columnMetadata,
  filterValue,
  filterOperators,
  setFilterMenuAnchor,
}: DateFilterInputProps<T>) => {
  'use memo';
  const columnId = header.column.id;
  const mode = resolveDateFilterMode(columnMetadata);
  const parsedFilterValue = React.useMemo(() => {
    const fieldType = mode === DATE_PICKER_MODE.DATE ? 'date' : 'datetime';
    const parsed = formatDateTimeForInput(filterValue, fieldType);
    return parsed instanceof Date ? parsed : null;
  }, [filterValue, mode]);

  return (
    <Box sx={filterCellBoxSx}>
      <OperatorIconButton
        columnId={columnId}
        filterOperators={filterOperators}
        filterValue={filterValue}
        onClick={(e) => setFilterMenuAnchor({ element: e.currentTarget, columnId })}
      />

      <Divider orientation="vertical" flexItem sx={filterDividerSx} />

      <CustomDatePicker
        value={parsedFilterValue}
        mode={mode}
        compact
        showDividerBeforeIcon
        onChange={(dateValue) => {
          const op = filterOperators[columnId] ?? '=';
          if (!dateValue) {
            header.column.setFilterValue(undefined);
            return;
          }
          const val =
            mode === DATE_PICKER_MODE.DATE
              ? toBackendDateString(dateValue)
              : toBackendDateTimeString(dateValue);
          header.column.setFilterValue({ op, val });
        }}
      />
    </Box>
  );
};

export const DateFilterInput = DateFilterInputComponent;
